/**
 * Snowflake nail designs — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_nails_shades`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const snowflakeNailDesignsPack: ToilePack = {
    slug: 'snowflake-nail-designs',
    category: 'beauty',
    name: 'Snowflake nail designs',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'blue', label: 'Icy blue', subject: 'icy blue short nails with white hand-painted snowflakes'},
        {key: 'white', label: 'White sweater', subject: 'milky white nails with silver snowflakes and a knit texture accent'},
        {key: 'red', label: 'Red and white', subject: 'red almond nails with a white snowflake on the ring finger'},
    ],
    styles: [
        {key: 'cosy', label: 'Cosy cocoa', style: 'wrapped around a mug of cocoa with a cream knit cuff at the wrist, warm soft glow from camera-right, a warm cosy balance', palette: 'Cream knit, warm brown cocoa and soft gold bokeh', framing: 'Seen from above at a slight angle, the mug and hand filling the lower two thirds'},
        {key: 'snow', label: 'In the snow', style: 'outdoors in gently falling snow, cool soft overcast daylight from above, a pale bright balance with a few flakes resting on the fingers', palette: 'White snow and cool blue-grey, the nails the warmest note', framing: 'A near-frontal close view, the bare hand raised against a snowy blur'},
        {key: 'tree', label: 'By the tree', style: 'beside a Christmas tree, warm soft glow from camera-left, a warm festive balance with the tree a soft green and gold blur', palette: 'Deep pine green and gold bokeh, a red ribbon', framing: 'A near-frontal close view, the hand holding a small wrapped gift'},
    ],
    directions: ['gold flakes', 'navy blue', 'toe nails'],
    directionHint: 'try: gold flakes',
    seo: {
        title: 'Snowflake Nail Designs: Blue, White, Red',
        metaDescription: 'Nine snowflake nail pictures for winter and Christmas: icy blue, white sweater and red and white sets in three settings.',
        h1: 'Snowflake nail designs: icy blue, white, red',
        tagline: 'Nine pictures of snowflake nails in three colourways, three winter settings each.',
        intro: 'Snowflake nails are hand-painted winter flakes for the cold months and Christmas. The nine pictures show icy blue nails with white snowflakes, milky white nails with silver flakes, and red almond nails with one white snowflake. Each set appears beside a cocoa mug, out in falling snow and by a Christmas tree. Type a different colour or a twist, such as gold flakes or navy blue, and the same settings carry it.',
        useCases: [
            'a winter manicure for the holidays',
            'a Christmas party nail idea',
            'a reference to show your nail technician',
            'a winter mood board',
        ],
        faq: [
            {q: 'What colours are in the set?', a: 'Icy blue with white snowflakes, milky white with silver flakes, and red almond nails with one white snowflake.'},
            {q: 'What are the three settings?', a: 'A cocoa mug with a cream knit cuff, a bare hand outdoors in falling snow, and a hand by a Christmas tree with a wrapped gift.'},
            {q: 'Can I ask for gold snowflakes?', a: 'Yes. Type it as a request. The picture is not promised to match it exactly.'},
            {q: 'Are these photographs of real nails?', a: 'No. They are generated images of one hand in each setting, not photographs of a particular person.'},
        ],
    },
}
